import React, { useImperativeHandle, forwardRef, useState, useEffect } from 'react';
import { useErrorInputs } from '../../../hooks/useErrorInputs';
import { useDataAuth } from '../../../hooks/useDataAuth';
import { useTranslation } from "react-i18next";
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import FormHelperText from '@mui/material/FormHelperText';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import moment from 'moment';
import { fetchServiceJson, fecthResolutions } from '../../../services/common.services';
export const CreditNoteForm = forwardRef(({ dto, supplierId, handleInputChange, handleDirectInputChange }, ref) => {
    const [errorInputs, , , validForm] = useErrorInputs({});
    const [{ userSession }] = useDataAuth();
    const { t } = useTranslation();
    const fieldsRequired = [
        'expedition_date',
        'resolution',
        'concept_correction_id',
        ...(dto.concept_correction_id === '5' ? ['concept_correction'] : [])
    ];
    const [conceptNotes, setConceptNotes] = useState([]);
    const [resolutions, setResolutions] = useState([]);
    
    useImperativeHandle(ref, () => ({
        getData: () => dto,
        errorInputs,
        validForm: () => validForm(fieldsRequired, dto)
    }));

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [creditNote, resolutionsData] = await Promise.all([
                    fetchServiceJson("MENU-CREDIT_NOTE", userSession.token),
                    fecthResolutions(supplierId, userSession.token)
                ]);
                if(creditNote?.data){
                    creditNote.data.forEach((item) => {
                        try {
                            const values = JSON.parse(item.value);
                            if (item.field === "concepts") {
                                setConceptNotes(values);
                            }
                        } catch (jsonError) {
                            console.error("Error parsing JSON for item:", item, jsonError);
                        }
                    });
                }
                if (resolutionsData?.data) {
                    setResolutions(resolutionsData?.data)
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };
        fetchData();
    }, [supplierId, userSession.token]);

    const handleChangeDate = (e, name) => handleDirectInputChange(name, moment(e).format('YYYY/MM/DD'));

    const handleChange = (e) => {
        const { name, value } = e.target;
        if(name === 'concept_correction_id'){
            const selectedConcept = conceptNotes.find(concept => concept.code === value);
            if (selectedConcept) {
                handleDirectInputChange('concept_correction_id', selectedConcept.code);
                if(selectedConcept.code === '5'){
                    handleDirectInputChange('concept_correction', '');
                    return
                }
                handleDirectInputChange('concept_correction', selectedConcept.name);
            }
        }
        handleDirectInputChange(name, value);
    };
    return (
        <Box sx={{ mb: 2 }}>
            <Grid container spacing={2}>
                <Grid size={{ xs:12,sm:1.5 }}>
                    <TextField fullWidth required size="small" label={t('commonFields.prefix')} name="prefix" 
                        error={Boolean(errorInputs.prefix?.isError)}
                        helperText={errorInputs.prefix?.messageError}
                        value={dto?.prefix} 
                        onChange={handleInputChange} 
                    />
                </Grid> 

                <Grid size={{ xs:12, sm:2.5 }}>
                    <TextField fullWidth disabled size="small" label={t('commonFields.documentId')} name="document_id" 
                        value={
                            dto?.document_id &&
                            (dto.document_id.toLowerCase().includes('assigned') ||dto.document_id.toLowerCase().includes('asignado'))
                            ? t('commonFields.defaultDocumentId')
                            : dto?.document_id || t('commonFields.defaultDocumentId')
                        }
                        onChange={handleInputChange} 
                    />
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker name="expedition_date" label={t('commonFields.expeditionDate')}
                            maxDate={new Date()}
                            value={dto.expedition_date}
                            onChange={(e) => handleChangeDate(e, 'expedition_date')}
                            renderInput={(params) => <TextField size="small" fullWidth InputLabelProps={{ shrink: true }} {...params} />}
                            inputFormat="dd/MM/yyyy"
                        />
                    </LocalizationProvider>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth required size="small" error={Boolean(errorInputs.resolution?.isError)}>
                        <InputLabel>{t('commonFields.resolutionNumber')}</InputLabel>
                        <Select name="resolution"
                            value={dto.resolution || ''}
                            onChange={handleInputChange}
                        >
                            {resolutions.map((resoltion, i) => <MenuItem key={i} value={resoltion.code}>{resoltion.name}</MenuItem>)}
                        </Select>
                        {errorInputs.resolution?.isError && (<FormHelperText>{errorInputs.resolution.messageError}</FormHelperText>)}
                    </FormControl>
                </Grid>

                <Grid size={{xs:12, md: dto?.concept_correction_id === '5' ? 1.5 : 4 }}>
                    <FormControl fullWidth required size="small" error={Boolean(errorInputs.concept_correction_id?.isError)}>
                        <InputLabel>{t('commonFields.conceptND')}</InputLabel>
                        <Select name="concept_correction_id" MenuProps={{PaperProps: {className: 'select-dropdown-scroll'}}}
                            value={dto.concept_correction_id || ''}
                            onChange={handleChange}
                        >   
                            {conceptNotes.map((conceptNote, i) => <MenuItem key={i} value={conceptNote.code}>{conceptNote.name}</MenuItem>)}
                        </Select>
                        {errorInputs.concept_correction_id?.isError && (<FormHelperText>{errorInputs.concept_correction_id.messageError}</FormHelperText>)}
                    </FormControl>
                </Grid> 

                {dto?.concept_correction_id === '5' && (
                    <Grid size={{ xs:12, sm:2.5 }}>
                        <TextField fullWidth required size="small" placeholder={t('commonFields.conceptND')} name="concept_correction" 
                            value={dto?.concept_correction} 
                            error={Boolean(errorInputs.concept_correction?.isError)}
                            helperText={errorInputs.concept_correction?.messageError}
                            onChange={handleInputChange} 
                        />
                    </Grid>
                )}

                <Grid size={{ xs: 12, md: 4 }}>
                    <TextField fullWidth size="small" label={t('commonFields.orderReference')} name="order_reference" type='number' className='input-no-spinner'
                        value={dto?.order_reference} 
                        onChange={handleInputChange} 
                    />
                </Grid>

                {dto?.order_reference !== '' && (
                    <Grid size={{ xs: 12, md: 4 }}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker name="order_reference_date" label={t('commonFields.orderReferenceDate')}
                                maxDate={new Date()}
                                value={dto.order_reference_date}
                                onChange={(e) => handleChangeDate(e, 'order_reference_date')}
                                renderInput={(params) => <TextField size="small" fullWidth InputLabelProps={{ shrink: true }} {...params} />}
                                inputFormat="dd/MM/yyyy"
                            />
                        </LocalizationProvider>
                    </Grid>
                )}
                
                {/* <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel>{t('commonFields.foreignCurrency')}</InputLabel>
                        <Select name="foreign_currency_value"
                            value={dto.foreign_currency_value || 'NO'}
                            onChange={handleInputChange}
                        >
                            <MenuItem value="NO">{t('no')}</MenuItem>
                            <MenuItem value="YES">{t('yes')}</MenuItem>
                        </Select>
                    </FormControl>
                </Grid> */}
            </Grid>
        </Box>
    );
});
